/**
 * The address the app was loaded from (BRD-050): on `<slug>.<base domain>` the workspace
 * is fixed to that tenant before anyone signs in - the sign-in page carries the tenant's
 * name and logo, the chooser is skipped, the switcher and the platform console are not
 * offered. On the base domain nothing changes. The server enforces the same rule on
 * every request; this store only shapes the UI around it.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api } from '@/app/api/client'

export interface HostTenant {
  id: string
  slug: string
  display_name: string
  logo_url: string | null
  favicon_url: string | null
  primary_color: string | null
}

interface HostInfo {
  base_domain: string | null
  tenant: HostTenant | null
}

export const useHostStore = defineStore('host', () => {
  const loaded = ref(false)
  const baseDomain = ref<string | null>(null)
  const tenant = ref<HostTenant | null>(null)
  /** true when the address is a tenant subdomain nobody serves (404 from the server) */
  const unknown = ref(false)

  const pinned = computed(() => tenant.value !== null)

  async function load(): Promise<void> {
    if (loaded.value) return
    try {
      const { data } = await api.get<HostInfo>('/public/host')
      baseDomain.value = data.base_domain
      tenant.value = data.tenant
    } catch (error) {
      unknown.value = (error as { status?: number }).status === 404
    } finally {
      loaded.value = true
    }
  }

  return { loaded, baseDomain, tenant, unknown, pinned, load }
})
